import React, { useState, useEffect } from 'react';
import { MenuItem, Select, FormControl, CircularProgress } from '@mui/material';
import styles from './StationsHealth.module.scss';
import { locales } from '../../constants';
import ContentDrill from '../composites/ContentDrill';
import { useUserAction } from '../../providers';
import { useAdminGyms } from '../../hooks/useAdminGyms';
import TabSlider, { TabSliderTab } from '../composites/TabSlider';
import Settings from './Settings';
import AssetThumbnail from '../atoms/AssetThumbnail';

import type { Gym, JetsonHealthDTO } from '../../types';

const texts = locales.en.components.stationsHealth;

enum ContentTabs {
  Settings = 'Settings',
}

const StationsHealth: React.FC = () => {
  const { gyms } = useAdminGyms();
  const { getJetsonsHealth } = useUserAction();

  const [selectedGymId, setSelectedGymId] = useState<string | null>(null);
  const [jetsonsHealth, setJetsonsHealth] = useState<JetsonHealthDTO[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!gyms.length) {
      setSelectedGymId(null);
      return;
    }
    setSelectedGymId((current) => {
      if (current && gyms.some((gym) => gym.id === current)) {
        return current;
      }
      return gyms[0].id;
    });
  }, [gyms]);

  useEffect(() => {
    if (!selectedGymId) {
      setJetsonsHealth([]);
      return;
    }
    let isActive = true;
    const fetchJetsonsHealth = async () => {
      setIsLoading(true);
      const data = await getJetsonsHealth(selectedGymId);
      if (isActive) {
        setJetsonsHealth(data);
        setIsLoading(false);
      }
    };
    fetchJetsonsHealth();
    return () => {
      isActive = false;
    };
  }, [selectedGymId, getJetsonsHealth]);

  const tabSliderTabs: TabSliderTab[] = [
    {
      label: ContentTabs.Settings,
      content: <Settings jetsonsHealth={jetsonsHealth} />,
    },
  ];

  const renderGymLogo = (gym: Gym) => {
    return (
      <div className={styles.logoContainer}>
        <AssetThumbnail
          src={gym.logoUrl}
          context="gym"
          fit="contain"
          className={styles.image}
          alt={`${gym.name} logo`}
        />
        <span className={styles.gymName}>{gym.name}</span>
      </div>
    );
  };

  const renderGymSelection = () => {
    return (
      <FormControl className={styles.gymSelection}>
        <Select
          value={selectedGymId ?? ''}
          onChange={(e) => setSelectedGymId(e.target.value as string)}
        >
          {gyms.map((gym) => (
            <MenuItem key={gym.id} value={gym.id}>
              {renderGymLogo(gym)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const renderContent = () => {
    if (isLoading || !gyms.length) {
      return (
        <div className={styles.loading}>
          <CircularProgress />
        </div>
      );
    }
    return (
      <>
        <div className={styles.controlsRow}>{renderGymSelection()}</div>
        <div className={styles.tabSliderWrapper}>
          <TabSlider
            tabs={tabSliderTabs}
            initialTab={0}
            rightActions={null}
            activeTab={0}
          />
        </div>
      </>
    );
  };

  return (
    <ContentDrill title={texts.title} disableSearch isParent>
      <div className={styles.stationsHealth}>{renderContent()}</div>
    </ContentDrill>
  );
};

export default StationsHealth;
